import {
  AppointmentSyncStatus,
  EligibilityScheduleStatus,
  EligibilityStatus,
  EligibilityStatusOverride,
  EligibilityVerificationSource
} from '../models/appointment.model';
import { InsuranceStatus, InsuranceStatusOverride } from '../models/insurance.model';

import { DxcIcon } from '@dxc/ui-components/icon/icon-types';
import { EligibilityDataSource } from '../models/data-source.model';
import { EligibilityErrorSetting } from '../models/eligibility.model';
import { PatientReVerificationPeriodUnit } from '../models/administration.model';
import { Status } from '../models/status.model';
import { UrlConstants } from './url.constants';
import { environment } from 'projects/portal-ui/src/environments/environment';

export class AppConstants {
  static readonly logLevel = environment.logLevel;
  static readonly eligibilityOAuthRequiredFeatures = ['EnhancedEligibility'];
  static readonly eligibilityCompleteFeature = 'EligibilityComplete';

  // localstorage
  static readonly localStorageLocationId = 'location';
  static readonly localStorageAutoLogin = 'autologin';
  static readonly localStorageIsFeatureRealtime = 'isFeatureRealTime';
  static readonly localStorageSelectedHuddleFilters = 'huddleFilters';
  static readonly localStorageUserEulaState = 'userEulaState';
  static readonly localStoragePayerCredentialPageFeature = 'payerCredentialPage';
  static readonly localStorageUserPilotSessionGroup = 'userPilotSessionGroup';

  // Idle - 30 Mins | Grace time - 2 Mins
  static readonly idleTimeInSeconds = 60 * 30;
  static readonly idleGraceTimeInSeconds = 60 * 2;
  static readonly maxIatOffset = 60 * 60;

  // Idle - 13 Mins | Refresh time - 14 Mins
  static readonly embedIdleTimeInSeconds = 60 * 13;
  static readonly refreshTimeInSeconds = 60 * 14;

  // UI
  static readonly toastDurationInMs = 3000;
  static readonly paginationLimit = 25;
  static readonly minDate = new Date(1900, 0, 1);

  // Schedule Settings
  static readonly maxDaysBeforeAppointment = 10;
  static readonly defaultDaysBeforeAppointment = 1;
  static readonly defaultTimeSlot = '21:00:00';
  static readonly defaultPatientReVerificationPeriod = 1;

  static readonly patientReVerificationPeriodMax: Record<PatientReVerificationPeriodUnit, number> =
    {
      [PatientReVerificationPeriodUnit.DAY]: 180,
      [PatientReVerificationPeriodUnit.MONTH]: 6
    } as const;

  // UI Status
  static readonly statusTypeMap: Record<string, Status> = {
    Active: 'success',
    Pending: 'unknown',
    Inactive: 'danger',
    Success: 'success',
    Failed: 'danger',
    Unknown: 'unknown',
    Valid: 'success',
    Disconnected: 'warning',
    Invalid: 'warning',
    InProgress: 'info',
    BatchFailed: 'danger',
    Error: 'danger',
    Verified: 'success',
    Cleared: 'primary',
    FollowUp: 'warning',
    NeedsAttention: 'danger',
    NoInsurance: 'unknown',
    Created: 'unknown',
    Queued: 'primary',
    Review: 'warning',
    Complete: 'success'
  } as const;

  static readonly statusIconMap: Record<string, DxcIcon> = {
    Active: 'check-circle-solid',
    Pending: 'clock-solid',
    Inactive: 'close-circle-solid',
    Success: 'check-circle-solid',
    Failed: 'close-circle-solid',
    Unknown: 'question-circle-solid',
    Valid: 'check-circle-solid',
    Disconnected: 'close-circle-solid',
    Invalid: 'question-circle-solid',
    InProgress: 'clock-solid',
    BatchFailed: 'close-circle-solid',
    Error: 'close-circle-solid',
    Verified: 'check-circle-solid',
    Cleared: 'check-circle',
    FollowUp: 'question-circle-solid',
    NeedsAttention: 'close-circle-solid',
    NoInsurance: 'question-circle-solid',
    Created: 'question-circle-solid',
    Queued: 'check-circle',
    Review: 'close-circle-solid',
    Complete: 'check-circle-solid'
  };

  static readonly syncStatusTextMap: Record<AppointmentSyncStatus, string> = {
    Invalid: 'Missing Data',
    Success: 'Success',
    BatchFailed: 'Sync Failed',
    Failed: 'Sync Failed',
    NoInsurance: 'No Insurance'
  };

  static readonly eligibilityStatusTextMap: Record<
    EligibilityStatus | EligibilityVerificationSource,
    string
  > = {
    InProgress: 'In Progress',
    Pending: 'Pending',
    Verified: 'Verified',
    Error: 'Error',
    Basic: 'Basic',
    Enhanced: 'Enhanced'
  };

  static readonly eligibilityStatusOverrideTextMap: Record<EligibilityStatusOverride, string> = {
    Cleared: 'Cleared',
    FollowUp: 'Follow Up',
    NeedsAttention: 'Needs Attention'
  };

  static readonly eligibilityScheduleStatusTextMap: Record<EligibilityScheduleStatus, string> = {
    Failed: 'Normalizing',
    Success: 'Normalizing',
    Retry: 'Retrying',
    Pending: '',
    InProgress: '',
    WaitingForConnection: '',
    WaitingForEligibility: '',
    NormalizationInProgress: '',
    Normalized: '',
    CleanedUp: ''
  };

  static readonly insuranceStatusTextMap: Record<InsuranceStatus, string> = {
    Active: 'Active Insurance',
    Inactive: 'Inactive Insurance',
    Pending: 'Pending',
    Unknown: 'Unknown'
  };

  static readonly insuranceStatusOverrideTextMap: Record<InsuranceStatusOverride, string> = {
    Active: 'Active',
    Cleared: 'Cleared',
    Inactive: 'Inactive',
    Unknown: 'Unknown'
  };

  // Enums and Options lists
  static readonly scheduleFrequency = {
    Monthly: 'Monthly',
    Weekly: 'Weekly',
    Daily: 'Daily'
  };

  static readonly scheduleFrequencyList = ['Monthly', 'Weekly', 'Daily'];

  static readonly insuranceStatusList = [
    {
      name: 'Active',
      value: 'Active'
    },
    {
      name: 'Inactive',
      value: 'Inactive'
    },
    {
      name: 'Pending',
      value: 'Pending'
    },
    {
      name: 'Unknown',
      value: 'Unknown'
    },
    {
      name: 'Cleared',
      value: 'Cleared'
    }
  ];

  static readonly statusList = [
    {
      name: 'Verified',
      value: 'Verified'
    },
    {
      name: 'In Progress',
      value: 'InProgress'
    },
    {
      name: 'Pending',
      value: 'Pending'
    },
    {
      name: 'Error',
      value: 'Error'
    },
    {
      name: 'Failed',
      value: 'Failed'
    },
    {
      name: 'Missing Data',
      value: 'Invalid'
    },
    {
      name: 'Follow Up',
      value: 'FollowUp'
    },
    {
      name: 'Cleared',
      value: 'Cleared'
    },
    {
      name: 'Needs Attention',
      value: 'NeedsAttention'
    },
    {
      name: 'No Insurance',
      value: 'NoInsurance'
    }
  ];

  static readonly subscriberRelationShipList = [
    {
      name: 'Spouse',
      value: '01'
    },
    {
      name: 'Self',
      value: '18'
    },
    {
      name: 'Child',
      value: '19'
    },
    {
      name: 'Others',
      value: '34'
    }
  ];

  static readonly subscriberRelationShip = {
    Spouse: '01',
    Self: '18',
    Child: '19',
    Others: '34'
  };

  // Explore the 'EligibilityErrorSetting' type definition for documentation
  static readonly eligibilityErrorSetting: EligibilityErrorSetting = {
    priority: {
      1: EligibilityDataSource.PayerPortal,
      2: EligibilityDataSource.EDI,
      3: EligibilityDataSource.ManualEntry
    },
    secondaryDataSources: [EligibilityDataSource.EDI],
    primaryDataSources: [EligibilityDataSource.PayerPortal],
    // Error that has to show a different message in pop-up modal
    // The error here is the Database 'error' column in ErrorType table
    // It is sent as 'code' to front-end
    errorToBeNotifiedPriority: {
      PayerCredNotFound: {
        priority: 1,
        eligibilityDataSource: EligibilityDataSource.PayerPortal,
        title: 'Payer Credential Not Added',
        type: 'info',
        errorMessage:
          'The payer for this appointment supports enhanced benefit information but the payer portal login is missing. Please add the credentials in the Administration section and re-run the eligibility.',
        content: 'Enhanced Eligibility is Supported',
        action: {
          buttonText: 'Add Payer Credential',
          navigateTo: UrlConstants.payerCredManagementUrl
        }
      },
      PayerCredInvalid: {
        priority: 2,
        eligibilityDataSource: EligibilityDataSource.PayerPortal,
        title: 'Invalid Payer Credential',
        type: 'danger',
        errorMessage:
          'The payer for this appointment supports enhanced benefit information but the payer portal login is invalid. Please update the credentials in the Administration section and re-run the eligibility.',
        content: 'Payer Credential is Invalid',
        action: {
          buttonText: 'Add Payer Credential',
          navigateTo: UrlConstants.payerCredManagementUrl
        }
      }
    }
  };

  static readonly defaultBenefitNetworkName = 'OTHER';
  static readonly defaultBenefitCategoryName = 'OTHER';

  static readonly networkMap: { [key: string]: string } = {
    inNetwork: 'In Network',
    outOfNetwork: 'Out of Network',
    networkNotApplicable: 'Network Not Applicable'
  };

  static readonly coverageLevelMap: { [key: string]: string } = {
    family: 'Family',
    individual: 'Individual',
    unknown: '-'
  };

  static readonly coverageLevelMapInverse: { [key: string]: string } = {
    Family: 'family',
    Individual: 'individual',
    '-': 'unknown'
  };

  static readonly periodMap: { [key: string]: string } = {
    serviceYear: 'Service Year',
    year: 'Year',
    month: 'Month',
    day: 'Day',
    lifetime: 'Lifetime',
    visit: 'Visit',
    coverageYear: 'Coverage Year'
  };

  static readonly periodMapInverse: { [key: string]: string } = {
    'Service Year': 'serviceYear',
    Year: 'year',
    Month: 'month',
    Day: 'day',
    Lifetime: 'lifetime',
    Visit: 'visit',
    'Coverage Year': 'coverageYear'
  };

  static readonly networkTypeList = ['inNetwork', 'outOfNetwork'];

  static readonly coverageLevelList = ['family', 'individual'];

  static readonly periodAliases: Record<string, string> = {
    unknown: 'Unknown Period',
    year: 'Year',
    Year: 'Year',
    CoverageYear: 'Year',
    coverageyear: 'Year',
    Coverageyear: 'Year',
    coverageYear: 'Year',
    'Coverage Year': 'Year',
    'Coverage year': 'Year',
    'coverage Year': 'Year',
    'coverage year': 'Year',
    CalendarYear: 'Year',
    calendaryear: 'Year',
    Calendaryear: 'Year',
    calendarYear: 'Year',
    'Calendar Year': 'Year',
    'Calendar year': 'Year',
    'calendar Year': 'Year',
    'calendar year': 'Year',
    coverage_year: 'Year',
    Coverage_Year: 'Year',
    Coverage_year: 'Year',
    coverage_Year: 'Year',
    ServiceYear: 'Service Year',
    Serviceyear: 'Service Year',
    serviceYear: 'Service Year',
    serviceyear: 'Service Year',
    'Service Year': 'Service Year',
    'Service year': 'Service Year',
    'service Year': 'Service Year',
    'service year': 'Service Year',
    service_year: 'Service Year',
    month: 'Month',
    Month: 'Month',
    day: 'Day',
    Day: 'Day',
    lifetime: 'Lifetime',
    Lifetime: 'Lifetime',
    visit: 'Visit',
    Visit: 'Visit'
  };

  // The list of recognized categories as in the order of priority to display.
  static readonly filterDentalCategories: string[] = ['dentalcare', 'diagnostic'];

  static readonly patientEligibilityHistoryAPI = {
    page: 1,
    limit: 5
  };

  // The configuration for fetching eligibility status after run/re-run eligibility.
  static readonly eligibilityStatusRetryConfig = {
    initialDelay: 1000,
    maxAttempts: 3,
    powerFactor: 2
  };

  static readonly backofficeBaseUrl = environment.backofficeBaseUrl;

  static readonly tooltipDelayInSeconds = 1;

  static readonly procedureCodeDisplayLimit = 4;

  static readonly invalidAppointmentSyncStatus = [
    AppointmentSyncStatus.Invalid,
    AppointmentSyncStatus.NoInsurance
  ];

  static readonly multipleActiveTemplatesErrorCode = 'DXC_1088';
  static readonly questionMaximumLength = 255;
  static readonly staredText = '********';
  static readonly invalidJWT = 'Invalid X-Auth-JWT provided';
}
